import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Alert, AlertGroup, AlertHierarchy, MockAlert } from '@/types/alerts'
import { RootState } from './store'

export interface AlertState {
  alerts: Alert[]
}

const initialState: AlertState = {
  alerts: [],
}

const alertSlice = createSlice({
  name: 'alert',
  initialState,
  reducers: {
    // Add a new alert
    addAlert: (
      state,
      action: PayloadAction<Omit<Alert, 'id' | 'timestamp' | 'isRead'>>
    ) => {
      const newAlert: Alert = {
        ...action.payload,
        id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        isRead: false,
      }
      state.alerts.unshift(newAlert) // Add to beginning
    },

    // Mark an alert as read
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload)
      if (alert) {
        alert.isRead = true
      }
    },

    // Clear all alerts
    clearAllAlerts: (state) => {
      state.alerts = []
    },

    // Init with mock data (for demo purposes)
    initializeMockAlerts: (state) => {
      const mockAlerts: MockAlert[] = [
        {
          message: 'Classic Latte sales dropped 8% compared to last week.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Classic - The original',
            tab: 'Overview',
          },
        },
        {
          message: 'Classic Latte sales dropped 8% compared to last week.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Classic - The original',
            tab: 'Overview',
          },
        },
        {
          message:
            'Revenue from Classic Latte decreased by $1,500 vs previous period.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Classic - The original',
            tab: 'Sales',
          },
        },
        {
          message: 'Latte + Cookie bundle down 18% compared to last month.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Classic - The original',
            tab: 'Bought Together',
          },
        },
        {
          message: 'Latte + Cookie bundle down 18% compared to last month.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Classic - The original',
            tab: 'Bought Together',
          },
        },
        {
          message: 'Aloha sales dropped 8% compared to last week.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Aloha - Chocolate Macadamia',
            tab: 'Overview',
          },
        },
        {
          message: 'Irish Dream sales dropped 8% compared to last week.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Irish Dream - Irish Cream',
            tab: 'Overview',
          },
        },
        {
          message: 'Caramel Macchiato sales dropped 8% compared to last week.',
          hierarchy: {
            clarityLens: 'Lattes',
            category: 'Caramel Macchiato',
            tab: 'Overview',
          },
        },
      ]

      // Clear existing and add mock alerts
      state.alerts = mockAlerts.map((mockAlert) => ({
        ...mockAlert,
        id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        isRead: false,
      }))
    },
  },
})

// Export actions
export const {
  addAlert,
  markAlertAsRead,
  clearAllAlerts,
  initializeMockAlerts,
} = alertSlice.actions

// Export reducer
export const alertReducer = alertSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

// Basic selector
export const selectAlerts = (state: RootState) => state.alert.alerts

// Select specific alert (memoized)
export const selectUnreadAlerts = createSelector(
  [selectAlerts],
  (alerts) => alerts.filter((alert) => !alert.isRead).length
)

// Filter alerts by hierarchy (factory selector - returns a function)
export const selectAlertsByHierarchy = (hierarchy: Partial<AlertHierarchy>) => {
  return createSelector([selectAlerts], (alerts) => {
    return alerts.filter((alert) => {
      const alertHierarchy = alert.hierarchy

      if (
        hierarchy.clarityLens &&
        alertHierarchy.clarityLens !== hierarchy.clarityLens
      ) {
        return false
      }
      if (
        hierarchy.salesLens &&
        alertHierarchy.salesLens !== hierarchy.salesLens
      ) {
        return false
      }
      if (
        hierarchy.category &&
        alertHierarchy.category !== hierarchy.category
      ) {
        return false
      }
      if (hierarchy.item && alertHierarchy.item !== hierarchy.item) {
        return false
      }
      if (hierarchy.tab && alertHierarchy.tab !== hierarchy.tab) {
        return false
      }

      return true
    })
  })
}

// Compute hierarchical alert groups (complex selector)
export const selectAlertGroups = createSelector(
  [selectAlerts],
  (alerts): AlertGroup[] => {
    // Initialize root groups
    const clarityLensGroups = new Map<string, AlertGroup>()

    alerts.forEach((alert) => {
      const { clarityLens, salesLens, category, item, tab } = alert.hierarchy

      // Create or get clarity lens group
      if (!clarityLensGroups.has(clarityLens)) {
        clarityLensGroups.set(clarityLens, {
          name: clarityLens,
          path: [clarityLens],
          alerts: [],
          subGroups: [],
          totalCount: 0,
          unreadCount: 0,
        })
      }
      const clarityGroup = clarityLensGroups.get(clarityLens)!

      // Create or get sales lens group
      let salesGroup: AlertGroup | undefined
      if (salesLens) {
        salesGroup = clarityGroup.subGroups.find((g) => g.name === salesLens)
        if (!salesGroup) {
          salesGroup = {
            name: salesLens,
            path: [clarityLens, salesLens],
            alerts: [],
            subGroups: [],
            totalCount: 0,
            unreadCount: 0,
          }
          clarityGroup.subGroups.push(salesGroup)
        }
      }

      // Create or get category group
      let categoryGroup: AlertGroup | undefined
      if (category) {
        const parentGroup = salesGroup || clarityGroup
        categoryGroup = parentGroup.subGroups.find((g) => g.name === category)
        if (!categoryGroup) {
          categoryGroup = {
            name: category,
            path: salesGroup
              ? [clarityLens, salesLens!, category]
              : [clarityLens, category],
            alerts: [],
            subGroups: [],
            totalCount: 0,
            unreadCount: 0,
          }
          parentGroup.subGroups.push(categoryGroup)
        }
      }

      // Create or get item group
      let itemGroup: AlertGroup | undefined
      if (item) {
        const parentGroup = categoryGroup || salesGroup || clarityGroup
        itemGroup = parentGroup!.subGroups.find((g) => g.name === item)
        if (!itemGroup) {
          itemGroup = {
            name: item,
            path: [...parentGroup!.path, item],
            alerts: [],
            subGroups: [],
            totalCount: 0,
            unreadCount: 0,
          }
          parentGroup!.subGroups.push(itemGroup)
        }
      }

      // Create or get tab group and add alert
      if (tab) {
        const parentGroup =
          itemGroup || categoryGroup || salesGroup || clarityGroup
        let tabGroup = parentGroup!.subGroups.find((g) => g.name === tab)
        if (!tabGroup) {
          tabGroup = {
            name: tab,
            path: [...parentGroup!.path, tab],
            alerts: [],
            subGroups: [],
            totalCount: 0,
            unreadCount: 0,
          }
          parentGroup!.subGroups.push(tabGroup)
        }
        tabGroup.alerts.push(alert)
      } else {
        // Add alert to the deepest available group
        const targetGroup =
          itemGroup || categoryGroup || salesGroup || clarityGroup
        targetGroup!.alerts.push(alert)
      }
    })

    // Calculate counts recursively
    const calculateCounts = (group: AlertGroup): void => {
      group.totalCount = group.alerts.length
      group.unreadCount = group.alerts.filter((alert) => !alert.isRead).length

      group.subGroups.forEach((subGroup) => {
        calculateCounts(subGroup)
        group.totalCount += subGroup.totalCount
        group.unreadCount += subGroup.unreadCount
      })
    }

    const result = Array.from(clarityLensGroups.values())
    result.forEach(calculateCounts)

    return result
  }
)
